import { collection, getDocs, doc, getDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { auth, db, model } from '../firebase';

export const getUserContent = async () => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return [];
    }

    const upperCaseRef = collection(db, `users/${userId}/Sessions`);
    const lowerCaseRef = collection(db, `users/${userId}/Sessions`);

    // Order by timestamp on the server (newest first)
    const upperQuery = query(upperCaseRef, orderBy('timestamp', 'desc'));
    const lowerQuery = query(lowerCaseRef, orderBy('timestamp', 'desc'));

    const [upperSnap, lowerSnap] = await Promise.all([
      getDocs(upperQuery),
      getDocs(lowerQuery)
    ]);

    const combined = [...upperSnap.docs, ...lowerSnap.docs];

    const uniqueDocsMap = new Map();
    combined.forEach(doc => {
      if (!uniqueDocsMap.has(doc.id)) {
        uniqueDocsMap.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp?.toDate() || new Date()
        });
      }
    });

    const contentArray = Array.from(uniqueDocsMap.values());

    return contentArray;

  } catch (error) {
    return [];
  }
};

export const getContentById = async (contentId) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId || !contentId) {
      return null;
    }
    
    const upperCaseRef = doc(db, `users/${userId}/Sessions`, contentId);
    const lowerCaseRef = doc(db, `users/${userId}/Sessions`, contentId);
    
    const [upperDoc, lowerDoc] = await Promise.all([
      getDoc(upperCaseRef).catch(() => null),
      getDoc(lowerCaseRef).catch(() => null)
    ]);
    
    const contentDoc = upperDoc?.exists() ? upperDoc : lowerDoc?.exists() ? lowerDoc : null;
    
    if (contentDoc && contentDoc.exists()) {
      return {
        id: contentDoc.id,
        ...contentDoc.data(),
        timestamp: contentDoc.data().timestamp?.toDate() || new Date()
      };
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

export const getAIResponseForSession = async (contentId, userMessage, selectedModel = 'Gemini 2.5 Flash') => {
  try {
    const user = auth.currentUser;
    if (!user || !contentId || !userMessage) {
      return "Missing user or content info.";
    }

    // 🔑 Get Firebase ID token
    const idToken = await user.getIdToken();

    // If parent didn't forward the current selection, prefer the global fallback set by ChatInput
    let uiModel = selectedModel;
    try {
      if ((!uiModel || uiModel === 'Gemini 2.5 Flash') && typeof window !== 'undefined' && window.__SELECTED_AIMODEL) {
        uiModel = window.__SELECTED_AIMODEL;
      }
    } catch (e) {
      // noop
    }

    // Simple normalized mapping from UI model names to backend identifiers
    let backendModel;
    const modelKey = (uiModel || "").toLowerCase().trim();
    
    // Mapping object: lowercase UI variants to backend models
    const modelMap = {
      "gemini 2.5 flash": "gemini-2.5-flash",
      "gemini-2.5-flash": "gemini-2.5-flash",
      "gemini flash": "gemini-2.5-flash",
      "flash": "gemini-2.5-flash",
      "gemini 2.5 flash lite": "gemini-2.5-flash-lite",
      "gemini-2.5-flash-lite": "gemini-2.5-flash-lite",
      "lite": "gemini-2.5-flash-lite",
      "gpt-5": "gpt-5",
      "gpt 5": "gpt-5",
      "gpt5": "gpt-5",
      "gpt": "gpt-5",
      "gpt-5-mini": "gpt-5-mini",
      "gpt-5 mini": "gpt-5-mini",
      "gpt5-mini": "gpt-5-mini",
      "mini": "gpt-5-mini",
      "grok-3-mini": "grok-3-mini",
      "gemini 2.5 pro": "gemini-2.5-pro",
      "gemini-2.5-pro": "gemini-2.5-pro",
      "gemini pro": "gemini-2.5-pro"
    };
    
    backendModel = modelMap[modelKey] || "gemini-2.5-flash-lite";  // Direct lookup with fallback

    const AI_RESPONSE_API = process.env.REACT_APP_AI_RESPONSE_API;
    const response = await fetch(
      AI_RESPONSE_API,
      { 
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
          contentId,
          userMessage,
          model: backendModel,
          backendModel: backendModel,
          uiModel: uiModel
        }),
      }
    );

    if (!response.ok) {
      console.error("AI response error:", response.statusText);
      return "AI service unavailable.";
    }

    const data = await response.json();
    return data.response || "No response generated.";
  } catch (error) {
    console.error("getAIResponseForSession error:", error);
    return "Error contacting AI service.";
  }
};


export const getChatMessages = (contentId, callback) => {
  const userId = auth.currentUser?.uid;
  if (!userId || !contentId) return () => {};

  const chatHistoryRef = collection(db, `users/${userId}/Sessions/${contentId}/ChatHistory`);
  const q = query(chatHistoryRef, orderBy("timestamp", "asc"));

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const messages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    }));
    callback(messages);
  }, (error) => {
  });

  return unsubscribe;
};

export const getSessionContext = async (contentId) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId || !contentId) return "";

    const chunksRef = collection(db, `users/${userId}/Sessions/${contentId}/Chunks`);
    const chunksQuery = query(chunksRef, orderBy("chunk_index", "asc"));

    const snapshot = await getDocs(chunksQuery);

const context = snapshot.docs
  .map(doc => {
    const data = doc.data();
    // Include start only if it exists
    return data.start ? `[Start: ${data.start}]\n${data.text || ""}` : data.text || "";
  })
  .join("\n\n");



    return context;
  } catch (error) {

    return "";
  }
};


const failedSessions = new Set();

export const generateSamplePromptsForSession = async (contentId) => {
  if (failedSessions.has(contentId)) {
    return [];
  }

  try {
    const context = await getSessionContext(contentId);
    if (!context) return [];

    const MAX_CONTEXT_LENGTH = 400; 
    let truncatedContext = context;
    
    if (context.length > MAX_CONTEXT_LENGTH) {
      const halfLength = Math.floor(MAX_CONTEXT_LENGTH / 2);
      truncatedContext = 
        context.substring(0, halfLength) + 
        "\n[...content truncated...]\n" + 
        context.substring(context.length - halfLength);
    }

    const instruction = `
You are an learning assistant that creates 3 short, actionable example questions a learner might ask about the following content. 
Respond with each example on its own line, keep each under 120 characters, and do NOT include numbering or extra commentary.

CONTENT:
${truncatedContext}
`.trim();

    // Single request to model
    const result = await model.generateContent(instruction);

    // Robustly obtain text from various SDK response shapes
    let text = '';
    try {
      const maybeText = result?.response?.text;
      if (typeof maybeText === 'function') {
        text = await result.response.text();
      } else if (typeof maybeText === 'string') {
        text = maybeText;
      } else if (typeof result?.text === 'function') {
        text = await result.text();
      } else if (typeof result === 'string') {
        text = result;
      } else {
        // last resort stringify
        text = String(maybeText ?? '');
      }
    } catch (e) {
      text = '';
    }
    
    // Process text, trim, filter empty, limit to 3
    const lines = String(text).split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const prompts = Array.from(new Set(lines)).slice(0, 3);

    return prompts;
  } catch (error) {
    console.error("Error generating sample prompts:", error);
    failedSessions.add(contentId);
    return [];
  }
};
